import {
  CalendarDays,
  Trophy,
  Users,
  Shirt,
  Dumbbell,
  Star,
  Target,
  CircleDot,
  Shield,
} from "lucide-react";
import "./PlayerBasketballPublic.css";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@api";
import { useDomain } from "@useDomain";
import DanImg from "../../image/default.png";
import PlayerAllTimeTab from "./Components/PlayerAllTimeTab";
import PlayerFeaturedStats from "./Components/PlayerFeaturedStats";
import PlayerSeasonSnapshot from "./Components/PlayerSeasonSnapshot";

function PlayerProfileDemo() {
  const [activeTab, setActiveTab] = useState("overview");
  const [playerData, setPlayerData] = useState(null);
  const [selectedTeamFilter, setSelectedTeamFilter] = useState("");
  const [trendStat, setTrendStat] = useState("PTS");
  const [showFullSchedule, setShowFullSchedule] = useState(false);
  const { id } = useParams();
  const { slug, domain } = useDomain();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const teamSltIdFromUrl = searchParams.get("teamSltId");

  useEffect(() => {
    const fetchPlayer = async () => {
      try {
        const res = await api.get(`/api/playersv2/public-profile/${id}`, {
          params: slug ? { slug, scope: "active" } : { domain, scope: "active" },
        });

        setPlayerData(res.data);
      } catch (err) {
        console.error("Error fetching player basketball profile:", err);
      }
    };

    if (id) fetchPlayer();
  }, [id, slug, domain]);

  const player = playerData?.player;
  const gameStats = playerData?.gameStats || [];
  
  const careerGameStats = playerData?.careerGameStats || [];


  const allStats = (playerData?.allStats || [])
    .filter((stat) => !stat.hidden)
    .sort(
      (a, b) =>
        (a.order ?? a.display_order ?? 999) -
        (b.order ?? b.display_order ?? 999)
    );

  const activeTeams =
    playerData?.teamHistory
      ?.filter((entry) => entry.season?.active)
      .map((entry) => ({
        id: entry.team?.id,
        sltId: entry.slt_id,
        teamName: entry.team?.name || "",
        leagueName: entry.league?.name || "",
        seasonName: entry.season?.name || "",
      }))
      .filter((team) => team.id) || [];

  const activeTeamsWithStats = useMemo(() => {
    return activeTeams.filter((team) => {
      return gameStats.some((gs) => {
        const game = gs.game;

        const homeSltId =
          game?.home_team_id ||
          game?.home_team?.id ||
          game?.home_team?.seasons_leagues_teams_id ||
          null;

        const awaySltId =
          game?.away_team_id ||
          game?.away_team?.id ||
          game?.away_team?.seasons_leagues_teams_id ||
          null;

        const teamPlayedInGame =
          Number(homeSltId) === Number(team.sltId) ||
          Number(awaySltId) === Number(team.sltId);

        return teamPlayedInGame && gs.stat_id;
      });
    });
  }, [activeTeams, gameStats]);



  useEffect(() => {
    if (!playerData) return;

    if (!activeTeamsWithStats.length) {
      setActiveTab("allTime");
    }
  }, [playerData, activeTeamsWithStats.length]);


  useEffect(() => {
    if (!activeTeamsWithStats.length) return;
    if (selectedTeamFilter) return;

    const urlTeamExists = activeTeamsWithStats.some(
      (team) => Number(team.sltId) === Number(teamSltIdFromUrl)
    );

    if (teamSltIdFromUrl && urlTeamExists) {
      setSelectedTeamFilter(teamSltIdFromUrl);
      return;
    }

    setSelectedTeamFilter(String(activeTeamsWithStats[0].sltId));
  }, [activeTeamsWithStats, teamSltIdFromUrl, selectedTeamFilter]);

  const primaryTeam =
    activeTeamsWithStats.find(
      (team) => Number(team.sltId) === Number(selectedTeamFilter)
    ) || activeTeamsWithStats[0];

  const numberAttr = player?.attributeValues?.find(
    (attr) => attr.attribute?.attribute_name === "Number"
  );

  const positionAttr = player?.attributeValues?.find(
    (attr) => attr.attribute?.attribute_name === "Position"
  );

  const careerUniqueGames = useMemo(() => {
    if (!careerGameStats.length) return [];

    return Array.from(
      new Map(
        careerGameStats.map((gameStat) => {
          const game = gameStat.game;

          const stats = allStats.map((stat) => {
            const total = careerGameStats
              .filter((gs) => {
                return gs.game_id === game?.id && gs.stat_id === stat.id;
              })
              .reduce((sum, gs) => sum + Number(gs.value || 0), 0);

            return {
              abbreviation: stat.abbreviation,
              value: total,
            };
          });

          return [
            game?.id,
            {
              id: game?.id,
              date: game?.date || game?.game_dateTime || null,
              time: game?.time || "",
              location: game?.location || "",
              homeTeam: game?.home_team?.team?.name || "Unknown",
              awayTeam: game?.away_team?.team?.name || "Unknown",
              stats,
              hasRecordedStats: careerGameStats.some(
                (gs) => gs.game_id === game?.id && gs.stat_id
              ),
              rawGame: game,
            },
          ];
        })
      ).values()
    ).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [careerGameStats, allStats]);

  const careerRecordedGames = useMemo(() => {
    return careerUniqueGames.filter((game) => game.hasRecordedStats);
  }, [careerUniqueGames]);

  const uniqueGames = useMemo(() => {
    if (!player || !gameStats.length) return [];

    return Array.from(
      new Map(
        gameStats.map((gameStat) => {
          const game = gameStat.game;

          const homeSltId =
            game?.home_team_id ||
            game?.home_team?.id ||
            game?.home_team?.seasons_leagues_teams_id ||
            null;

          const awaySltId =
            game?.away_team_id ||
            game?.away_team?.id ||
            game?.away_team?.seasons_leagues_teams_id ||
            null;

          const stats = allStats.map((stat) => {
            const total = gameStats
              .filter((gs) => {
                const statSltId =
                  gs.roster?.seasons_leagues_teams_id ||
                  gs.roster?.seasons_leagues_team?.id ||
                  null;

                return (
                  gs.game_id === game?.id &&
                  gs.stat_id === stat.id &&
                  Number(statSltId) === Number(selectedTeamFilter)
                );
              })
              .reduce((sum, gs) => sum + Number(gs.value || 0), 0);

            return {
              abbreviation: stat.abbreviation,
              value: total,
            };
          });

          return [
            game?.id,
            {
              id: game?.id,
              date: game?.date || game?.game_dateTime || null,
              time: game?.time || "",
              location: game?.location || "",
              homeSltId,
              awaySltId,
              homeTeam: game?.home_team?.team?.name || "Unknown",
              awayTeam: game?.away_team?.team?.name || "Unknown",
              stats,
              hasRecordedStats: gameStats.some(
                (gs) => gs.game_id === game?.id && gs.stat_id
              ),
              rawGame: game,
            },
          ];
        })
      ).values()
    ).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [player, gameStats, allStats, selectedTeamFilter]);




  

  const filteredGames = useMemo(() => {
    if (!selectedTeamFilter) return [];

    return uniqueGames.filter(
      (game) =>
        Number(game.homeSltId) === Number(selectedTeamFilter) ||
        Number(game.awaySltId) === Number(selectedTeamFilter)
    );
  }, [uniqueGames, selectedTeamFilter]);

  const recordedGames = useMemo(() => {
    return filteredGames.filter((game) => game.hasRecordedStats);
  }, [filteredGames]);

  const getCareerStatFromGame = (game, abbreviation) => {
    return game?.stats?.find((s) => s.abbreviation === abbreviation)?.value || 0;
  };

  const careerGetTotalStatValue = (abbreviation) => {
    return careerRecordedGames.reduce((sum, game) => {
      return sum + Number(getCareerStatFromGame(game, abbreviation) || 0);
    }, 0);
  };

  const getStatFromGame = (game, abbreviation) => {
    return game?.stats?.find((s) => s.abbreviation === abbreviation)?.value || 0;
  };

  const getTotalStatValue = (abbreviation) => {
    return recordedGames.reduce((sum, game) => {
      return sum + Number(getStatFromGame(game, abbreviation) || 0);
    }, 0);
  };

  const careerGamesPlayed = careerRecordedGames.length;
  const careerTotalPTS = careerGetTotalStatValue("PTS");
  const careerTotalFTM = careerGetTotalStatValue("FTM");
  const careerTotalFTA = careerGetTotalStatValue("FTA");
  const careerTotal3PM = careerGetTotalStatValue("3PM");


  const gamesPlayed = recordedGames.length;
  const totalPTS = getTotalStatValue("PTS");
  const totalFTM = getTotalStatValue("FTM");
  const totalFTA = getTotalStatValue("FTA");
  const total3PM = getTotalStatValue("3PM");

  const ftPercentage = totalFTA
    ? ((totalFTM / totalFTA) * 100).toFixed(1)
    : "0.0";

  const careerBestGame = careerRecordedGames
    .slice()
    .sort(
      (a, b) =>
        getCareerStatFromGame(b, "PTS") - getCareerStatFromGame(a, "PTS")
    )[0];


  const bestGame = recordedGames
    .slice()
    .sort((a, b) => getStatFromGame(b, "PTS") - getStatFromGame(a, "PTS"))[0];



  const trendStatOptions = [
    { label: "Points", value: "PTS" },
    { label: "3 Points Made", value: "3PM" },
  ];

  const selectedTrendLabel =
    trendStatOptions.find((option) => option.value === trendStat)?.label ||
    "Points";

  const isAllTimeTab = activeTab === "allTime";

  const completedGames = filteredGames.filter((game) => {
    if (!game.date) return false;

    const gameDate = new Date(`${game.date}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return gameDate < today;
  });

  const recentGames = showFullSchedule ? filteredGames : completedGames.slice(0, 10);

const chartPoints = completedGames
  .slice(0, 10)
  .reverse()
  .map((game) => ({
    label: game.date
      ? new Date(`${game.date}T00:00:00`).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        })
      : "TBD",
    value: getStatFromGame(game, trendStat),
  }));

  const chartMaxValue = Math.max(
    20,
    1,
    ...chartPoints.map((point) => Number(point.value) || 0)
  );

  const chartWidth = 560;
  const chartHeight = 210;
  const chartLeft = 42;
  const chartRight = 540;
  const chartTop = 28;
  const chartBottom = 168;
  const chartInnerWidth = chartRight - chartLeft;
  const chartInnerHeight = chartBottom - chartTop;

  const getChartX = (index) => {
    if (chartPoints.length <= 1) return chartLeft;
    return chartLeft + (index / (chartPoints.length - 1)) * chartInnerWidth;
  };

  const getChartY = (value) => {
    return chartBottom - ((Number(value) || 0) / chartMaxValue) * chartInnerHeight;
  };

  const polylinePoints = chartPoints
    .map((point, index) => `${getChartX(index)},${getChartY(point.value)}`)
    .join(" ");

  const nextGame =
    filteredGames
      .filter((game) => {
        if (!game.date) return false;

        const gameDate = new Date(game.date);
        const now = new Date();

        return gameDate >= now;
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date))[0] || null;



  const heroTeamLabel = isAllTimeTab ? "All Teams" : primaryTeam?.teamName;
  const heroSeasonLabel = isAllTimeTab ? "All-Time" : primaryTeam?.seasonName;
  const heroBestGame = isAllTimeTab ? careerBestGame : bestGame;

  const heroGamesPlayed = isAllTimeTab ? careerGamesPlayed : gamesPlayed;
  const heroGetTotalStatValue = isAllTimeTab
    ? careerGetTotalStatValue
    : getTotalStatValue;

  const formatDate = (date) => {
    if (!date) return "TBD";

    return new Date(`${date}T00:00:00`).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (time) => {
    if (!time) return "";

    const [hourRaw, minuteRaw] = String(time).split(":");
    const hour = Number(hourRaw);
    const minute = minuteRaw || "00";

    if (Number.isNaN(hour)) return time;

    const displayHour = hour % 12 || 12;
    const suffix = hour >= 12 ? "PM" : "AM";

    return `${displayHour}:${minute} ${suffix}`;
  };

  const getMatchup = (game) => {
    if (!game) return "Opponent TBD";
    return `${game.homeTeam} vs ${game.awayTeam}`;
  };

  if (!playerData || !player) return null;

  return (
    <div className="player-demo-page">
      <main>
        <section className="player-hero">
          <div className="player-photo-area">
            <div className="player-photo-placeholder">
              <img
                src={player.image || DanImg}
                alt={`${player.firstName} ${player.lastName}`}
                className="player-photo-img"
              />
            </div>
          </div>

          <div className="player-hero-content">
            <h1>
              {player.firstName} {player.lastName || ""}
            </h1>

            <div className="player-tags">
              {heroTeamLabel && !isAllTimeTab && (
                <span
                  className="player-team-tag-clickable"
                  onClick={() =>
                    navigate(
                      slug
                        ? `/${slug}/teams/${primaryTeam.id}`
                        : `/teams/${primaryTeam.id}`
                    )
                  }
                >
                  <Shield size={16} />
                  {heroTeamLabel}
                </span>
              )}

              {/* {primaryTeam?.leagueName && (
                <span>
                  <Trophy size={16} />
                  {selectedTeamFilter === "all"
                    ? "All Active Leagues"
                    : primaryTeam.leagueName}
                </span>
              )} */}

              {heroSeasonLabel && (
                <span>
                  <CalendarDays size={16} />
                  {heroSeasonLabel}
                </span>
              )}
            </div>

            {!isAllTimeTab && activeTeamsWithStats.length > 1 && (
              <div className="player-team-filter-row">
                {/* <label htmlFor="playerTeamFilter">View Stats:</label> */}

                <select
                  id="playerTeamFilter"
                  value={selectedTeamFilter}
                  onChange={(e) => setSelectedTeamFilter(e.target.value)}
                >
                  {/* <option value="all">All Active Teams</option> */}

                  {activeTeamsWithStats.map((team) => (
                    <option key={`${team.id}-${team.sltId}`} value={team.sltId}>
                      {team.teamName} — {team.leagueName}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="hero-side">
            {!isAllTimeTab && (
              <div className="next-game-card">
                <p>NEXT GAME</p>

                {nextGame ? (
                  <>
                    <h3>{getMatchup(nextGame)}</h3>

                    <span>
                      {formatDate(nextGame.date)}
                      {nextGame.time ? ` · ${formatTime(nextGame.time)}` : ""}
                    </span>

                    <small>
                      {nextGame.location ||
                        primaryTeam?.leagueName ||
                        "Location TBD"}
                    </small>

                    <div className="matchup-mini">
                      <div>
                        <div className="team-circle orange">
                          {nextGame.homeTeam?.charAt(0) || "H"}
                        </div>
                        <strong>
                          {nextGame.homeTeam?.slice(0, 3).toUpperCase()}
                        </strong>
                      </div>

                      <span>vs</span>

                      <div>
                        <div className="team-circle navy">
                          {nextGame.awayTeam?.charAt(0) || "A"}
                        </div>
                        <strong>
                          {nextGame.awayTeam?.slice(0, 3).toUpperCase()}
                        </strong>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          slug
                            ? `/${slug}/games/${nextGame.id}`
                            : `/games/${nextGame.id}`
                        )
                      }
                    >
                      View Matchup
                    </button>
                  </>
                ) : (
                  <>
                    <h3>No upcoming game</h3>
                    <span>Schedule has not been added.</span>
                  </>
                )}
              </div>
            )}
            <div
              className={`best-game-card ${isAllTimeTab ? "alltime-best-game-card" : ""} ${heroBestGame ? "clickable-card" : ""
                }`}
              onClick={() => {
                if (!heroBestGame) return;

                navigate(
                  slug
                    ? `/${slug}/games/${heroBestGame.id}`
                    : `/games/${heroBestGame.id}`
                );
              }}
            >
              <Star size={30} />

              <div>
                <p>{isAllTimeTab ? "CAREER HIGH" : "BEST GAME"}</p>

                <h3>
                  {heroBestGame
                    ? isAllTimeTab
                      ? getCareerStatFromGame(heroBestGame, "PTS")
                      : getStatFromGame(heroBestGame, "PTS")
                    : 0}{" "}
                  <span>PTS</span>
                </h3>

                <small>
                  {heroBestGame
                    ? `${formatDate(heroBestGame.date)} · ${getMatchup(heroBestGame)}`
                    : "No games yet"}
                </small>
              </div>
            </div>
          </div>

          <PlayerFeaturedStats
            gamesPlayed={heroGamesPlayed}
            getTotalStatValue={heroGetTotalStatValue}
          />
        </section>

        <div className="profile-tabs">
          {activeTeamsWithStats.length > 0 && (
            <button
              type="button"
              className={activeTab === "overview" ? "active" : ""}
              onClick={() => setActiveTab("overview")}
            >
              Overview
            </button>
          )}

          <button
            type="button"
            className={activeTab === "allTime" ? "active" : ""}
            onClick={() => setActiveTab("allTime")}
          >
            All-Time
          </button>
        </div>

        {activeTab === "overview" && (
          <section className="profile-grid">
            <div className="profile-left-rail">
              <div className="profile-card about-card">
                <h3>About {player.firstName}</h3>

                {positionAttr?.value && (
                  <div className="info-row">
                    <Dumbbell size={16} />
                    <span>Position</span>
                    <strong>{positionAttr.value}</strong>
                  </div>
                )}

                {numberAttr?.value && (
                  <div className="info-row">
                    <Shirt size={16} />
                    <span>Jersey Number</span>
                    <strong>#{numberAttr.value}</strong>
                  </div>
                )}

                <div className="info-row">
                  <Shield size={16} />
                  <span>Team</span>
                  <strong>
                    {primaryTeam?.teamName || "—"}
                  </strong>
                </div>

                <div className="info-row">
                  <CircleDot size={16} />
                  <span>League</span>
                  <strong>
                    {primaryTeam?.leagueName || "—"}
                  </strong>
                </div>

                <div className="info-row">
                  <Users size={16} />
                  <span>Season</span>
                  <strong>{primaryTeam?.seasonName || "—"}</strong>
                </div>
              </div>

              <div className="profile-card highlights-card">
                <h3>Player Highlights</h3>

                <div className="highlight-row">
                  <Star />

                  <div>
                    <strong>Season High</strong>
                    <p>
                      {bestGame
                        ? `${getStatFromGame(
                          bestGame,
                          "PTS"
                        )} points on ${formatDate(bestGame.date)}`
                        : "No season high yet"}
                    </p>
                  </div>
                </div>

                <div className="highlight-row">
                  <Target />

                  <div>
                    <strong>Free Throw Line</strong>
                    <p>{ftPercentage}% from the free throw line in this view</p>
                  </div>
                </div>

                <div className="highlight-row last">
                  <CircleDot />

                  <div>
                    <strong>3PT Production</strong>
                    <p>{total3PM} three-pointers made in this view</p>
                  </div>
                </div>
              </div>

              <PlayerSeasonSnapshot
                selectedTeamFilter={selectedTeamFilter}
                primaryTeam={primaryTeam}
                gamesPlayed={gamesPlayed}
                totalPTS={totalPTS}
                totalFTM={totalFTM}
                totalFTA={totalFTA}
                total3PM={total3PM}
              />
            </div>

            <div className="profile-main-content">
              <div className="profile-card trend-card">
                <div className="card-header">
                  <h3>Performance Trend</h3>

                  <select
                    value={trendStat}
                    onChange={(e) => setTrendStat(e.target.value)}
                  >
                    {trendStatOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="chart-wrap">
                  {chartPoints.length ? (
                    <svg
                      viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                      className="line-chart"
                      aria-label={`${selectedTrendLabel} performance trend`}
                    >
                      <text x="18" y="33" className="axis-label">
                        20
                      </text>
                      <text x="18" y="68" className="axis-label">
                        15
                      </text>
                      <text x="18" y="103" className="axis-label">
                        10
                      </text>
                      <text x="23" y="138" className="axis-label">
                        5
                      </text>
                      <text x="23" y="173" className="axis-label">
                        0
                      </text>

                      <line x1="42" y1="28" x2="540" y2="28" />
                      <line x1="42" y1="63" x2="540" y2="63" />
                      <line x1="42" y1="98" x2="540" y2="98" />
                      <line x1="42" y1="133" x2="540" y2="133" />
                      <line x1="42" y1="168" x2="540" y2="168" />

                      <polyline points={polylinePoints} />

                      {chartPoints.map((point, index) => {
                        const x = getChartX(index);
                        const y = getChartY(point.value);

                        return (
                          <g key={`${point.label}-${index}`}>
                            <circle cx={x} cy={y} r="5" />
                            <text x={x} y={y - 13}>
                              {point.value}
                            </text>
                            <text x={x} y="195" className="chart-label">
                              {point.label}
                            </text>
                          </g>
                        );
                      })}
                    </svg>
                  ) : (
                    <div className="empty-chart-state">
                        No completed games yet.
                    </div>
                  )}

                  <p>Last 10 Games · {selectedTrendLabel}</p>
                </div>
              </div>

              <div className="profile-card game-log-card">
                <div className="card-header">
                  <h3>Recent Games</h3>
                </div>

                <div className="game-log-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Matchup</th>
                        <th>PTS</th>
                        <th>FTM</th>
                        <th>FTA</th>
                        <th>FT%</th>
                        <th>3PM</th>
                      </tr>
                    </thead>

                    <tbody>
                      {recentGames.map((game) => {
                        const ftm = getStatFromGame(game, "FTM");
                        const fta = getStatFromGame(game, "FTA");
                        const ft = fta ? (ftm / fta).toFixed(3) : "0";

                        return (
                          <tr key={game.id}>
                            <td>{formatDate(game.date)}</td>

                            <td
                              className="players-profile-clickable-row"
                              onClick={() =>
                                navigate(
                                  slug
                                    ? `/${slug}/games/${game.id}`
                                    : `/games/${game.id}`
                                )
                              }
                            >
                              {getMatchup(game)}
                            </td>

                            <td>{getStatFromGame(game, "PTS")}</td>
                            <td>{ftm}</td>
                            <td>{fta}</td>
                            <td>{ft}</td>
                            <td>{getStatFromGame(game, "3PM")}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="table-footer">
                  <span>
                    {showFullSchedule
                      ? `Showing full schedule: ${recentGames.length} game${recentGames.length === 1 ? "" : "s"}`
                      : `Showing latest ${recentGames.length} completed game${recentGames.length === 1 ? "" : "s"}`}
                  </span>

                  {primaryTeam?.id && (
                    <button type="button" onClick={() => setShowFullSchedule((prev) => !prev)}>
                      {showFullSchedule ? "Show Recent Games" : "View Full Schedule"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {activeTab === "allTime" && (
          <PlayerAllTimeTab
            player={player}
            teamHistory={playerData?.teamHistory || []}
            gameStats={playerData?.careerGameStats || []}
            allStats={allStats}
            onGameClick={(gameId) =>
              navigate(slug ? `/${slug}/games/${gameId}` : `/games/${gameId}`)
            }
          />
        )}
      </main>
    </div>
  );
}

export default PlayerProfileDemo;